import { affiliateApi, brandApi } from "@/lib/axios";
import type {
  ContentProgramConfig,
  MetaMediaType,
} from "@/config/contentProgramUpload";

export type { ContentProgramConfig };

export interface ProgramSample {
  id: string;
  programId: string;
  mediaType: MetaMediaType;
  mediaUrl: string;
  thumbnailUrl: string | null;
  sortOrder: number;
  createdAt: string;
}

export interface ContentProgramProductSummary {
  id: string;
  title: string;
  image: string | null;
}

export interface ContentProgramBrandRow {
  id: string;
  brandDetailId: string;
  title: string;
  commissionRate: number;
  description: string | null;
  isOpenToAllEnrolledAffiliates: boolean;
  status: string;
  contentConfig?: ContentProgramConfig | null;
  entitledProductIds?: string[];
  createdAt: string;
  updatedAt: string;
  _count?: {
    contents: number;
    allowedAffiliates: number;
    samples?: number;
  };
}

export interface ContentProgramDetail extends ContentProgramBrandRow {
  allowedAffiliates: Array<{
    programId: string;
    affiliateId: string;
    createdAt: string;
    affiliate: {
      id: string;
      firstName: string | null;
      lastName: string | null;
      email: string;
      username: string | null;
    };
  }>;
  samples?: ProgramSample[];
}

export interface ContentSubmissionRow {
  id: string;
  metaAdsCampaignProgramId: string;
  brandDetailId: string;
  creatorAffiliateId: string | null;
  title: string | null;
  mediaType: string | null;
  mediaUrl: string | null;
  thumbnailUrl: string | null;
  destinationUrl: string | null;
  productIds?: string[];
  products?: ContentProgramProductSummary[];
  status: string;
  reviewMessage: string | null;
  createdAt: string;
  updatedAt: string;
  creatorAffiliate?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    username: string | null;
  } | null;
  /** DRAFT rows are prepared by a TikTok ad request; nothing runs yet. */
  launchedAd?: { id: string; metaAdId: string | null; status?: string; adConfig?: DraftAdConfig | null } | null;
  /** Set when this submission answers a brand's TikTok ad request. */
  tiktokAdRequest?: ContentAdRequestSummary | null;
}

export interface DraftAdConfig {
  campaignName?: string;
  objective?: "OUTCOME_TRAFFIC" | "OUTCOME_SALES";
  dailyBudget?: number;
  countries?: string[];
  ageMin?: number;
  ageMax?: number;
  facebookPageId?: string | null;
  targetCampaignId?: string | null;
  targetAdsetId?: string | null;
}

export interface ContentAdRequestSummary {
  id: string;
  status: string;
  creatorUsername?: string;
  flatFee: number | string | null;
  commissionRate: number | null;
  checklist?: string[];
  notes?: string | null;
}

/** A submission is live on Meta only once its ad row left DRAFT. */
export function isRunningAd(row: Pick<ContentSubmissionRow, "status" | "launchedAd">): boolean {
  return row.status === "IN_USE" || Boolean(row.launchedAd && row.launchedAd.status !== "DRAFT");
}

export interface EligibleContentProgramRow extends ContentProgramBrandRow {
  brandDetail: {
    id: string;
    companyName: string | null;
    firstName: string;
    lastName: string;
    username: string | null;
    brandProfileImage: string | null;
  };
  samples?: ProgramSample[];
}

export interface SubmitContentBody {
  title: string;
  /** The TikTok ad request this upload answers. */
  adRequestId?: string;
  productIds: string[];
  mediaUrl: string;
  thumbnailUrl?: string;
  mediaType: MetaMediaType;
  mimeType: string;
  byteSize?: number;
  width?: number;
  height?: number;
  durationSeconds?: number;
  aspectRatio?: string;
}

export async function listBrandContentPrograms(): Promise<ContentProgramBrandRow[]> {
  const { data } = await brandApi.get<ContentProgramBrandRow[]>(
    "/meta-ads/campaign-programs",
  );
  return data;
}

export async function getBrandContentProgram(
  programId: string,
): Promise<ContentProgramDetail> {
  const { data } = await brandApi.get<ContentProgramDetail>(
    `/meta-ads/campaign-programs/${programId}`,
  );
  return data;
}

export async function createBrandContentProgram(body: {
  title: string;
  commissionRate: number;
  description?: string;
  isOpenToAllEnrolledAffiliates?: boolean;
  allowedAffiliateIds?: string[];
  contentConfig?: ContentProgramConfig;
  entitledProductIds?: string[];
}): Promise<ContentProgramDetail> {
  const { data } = await brandApi.post<ContentProgramDetail>(
    "/meta-ads/campaign-programs",
    body,
  );
  return data;
}

export async function updateBrandContentProgram(
  programId: string,
  body: Partial<{
    title: string;
    commissionRate: number;
    description: string | null;
    isOpenToAllEnrolledAffiliates: boolean;
    allowedAffiliateIds: string[];
    contentConfig: ContentProgramConfig | null;
    entitledProductIds: string[];
  }>,
): Promise<ContentProgramDetail> {
  const { data } = await brandApi.put<ContentProgramDetail>(
    `/meta-ads/campaign-programs/${programId}`,
    body,
  );
  return data;
}

export async function listProgramSamples(
  programId: string,
  asAffiliate = false,
): Promise<ProgramSample[]> {
  const api = asAffiliate ? affiliateApi : brandApi;
  const { data } = await api.get<ProgramSample[]>(
    `/meta-ads/campaign-programs/${programId}/samples`,
  );
  return data;
}

export async function addProgramSample(
  programId: string,
  body: {
    mediaUrl: string;
    thumbnailUrl?: string;
    mediaType: MetaMediaType;
    sortOrder?: number;
  },
): Promise<ProgramSample> {
  const { data } = await brandApi.post<ProgramSample>(
    `/meta-ads/campaign-programs/${programId}/samples`,
    body,
  );
  return data;
}

export async function deleteProgramSample(
  programId: string,
  sampleId: string,
): Promise<{ ok: boolean }> {
  const { data } = await brandApi.delete<{ ok: boolean }>(
    `/meta-ads/campaign-programs/${programId}/samples/${sampleId}`,
  );
  return data;
}

export async function listProgramSubmissions(
  programId: string,
  status?: string,
): Promise<ContentSubmissionRow[]> {
  const { data } = await brandApi.get<ContentSubmissionRow[]>(
    `/meta-ads/campaign-programs/${programId}/submissions`,
    { params: status ? { status } : undefined },
  );
  return data;
}

export async function reviewProgramSubmission(
  programId: string,
  contentId: string,
  body: {
    action: "APPROVE" | "REJECT" | "REQUEST_CHANGE";
    message?: string;
  },
): Promise<ContentSubmissionRow> {
  const { data } = await brandApi.post<ContentSubmissionRow>(
    `/meta-ads/campaign-programs/${programId}/submissions/${contentId}/review`,
    body,
  );
  return data;
}

export interface ProgramDraftAd {
  id: string;
  status: string;
  adConfig: DraftAdConfig | null;
  createdAt: string;
  content: ContentSubmissionRow;
}

export async function listProgramDrafts(programId: string): Promise<ProgramDraftAd[]> {
  const { data } = await brandApi.get<ProgramDraftAd[]>(`/meta-ads/campaign-programs/${programId}/drafts`);
  return data;
}

export async function listProgramProducts(
  programId: string,
  params?: { cursor?: string; limit?: number },
): Promise<{
  products: ContentProgramProductSummary[];
  pagination: {
    hasNextPage: boolean;
    endCursor: string | null;
    total: number;
  };
}> {
  const { data } = await affiliateApi.get(
    `/meta-ads/campaign-programs/${programId}/products`,
    { params },
  );
  return data;
}

/** Affiliate */
export async function listAffiliateEligiblePrograms(): Promise<
  EligibleContentProgramRow[]
> {
  const { data } = await affiliateApi.get<EligibleContentProgramRow[]>(
    "/meta-ads/campaign-programs/me/eligible",
  );
  return data;
}

export async function listAffiliateMySubmissions(): Promise<
  (ContentSubmissionRow & {
    metaAdsCampaignProgram: {
      id: string;
      title: string;
      commissionRate: number;
      brandDetail: {
        companyName: string | null;
        firstName: string;
        lastName: string;
        username: string | null;
      };
    };
  })[]
> {
  const { data } = await affiliateApi.get(
    "/meta-ads/campaign-programs/me/submissions",
  );
  return data;
}

export async function submitAffiliateContent(
  programId: string,
  body: SubmitContentBody,
): Promise<ContentSubmissionRow> {
  const { data } = await affiliateApi.post<ContentSubmissionRow>(
    `/meta-ads/campaign-programs/${programId}/submit`,
    body,
  );
  return data;
}

export async function retractAffiliateSubmission(
  contentId: string,
): Promise<{ ok: boolean }> {
  const { data } = await affiliateApi.delete<{ ok: boolean }>(
    `/meta-ads/campaign-programs/me/submissions/${contentId}`,
  );
  return data;
}

export async function resubmitAffiliateSubmission(
  contentId: string,
  body: SubmitContentBody,
): Promise<ContentSubmissionRow> {
  const { data } = await affiliateApi.put<ContentSubmissionRow>(
    `/meta-ads/campaign-programs/me/submissions/${contentId}/resubmit`,
    body,
  );
  return data;
}
